import { createTheme, Theme } from '@mui/material/styles';
import { PortalBrandingOut } from './types/apiRequests';

const SAFE_FONT_FAMILY = /^[a-zA-Z0-9 \-]+$/;
const isSafeFontFamily = (value: string | null | undefined): boolean =>
  !!value && SAFE_FONT_FAMILY.test(value);

const DEFAULTS = {
  // Brand primary — xxllnc.ai purple (was near-black #28282C)
  primary: '#4B2E83',
  // Body/heading ink — kept near-black so the purple accent stays an accent,
  // not the colour of all text.
  ink: '#28282C',
  secondary: '#E86E44',
  success: '#007347',
  successLight: '#e8f5ef',
  error: '#C24242',
  errorLight: '#fef2f2',
  warningLight: '#fff7ed',
  info: '#2C4A69',
  infoLight: '#f0f4f8',
  background: '#fafafa',
  textSecondary: '#71717a',
};

export function createPortalTheme(branding?: PortalBrandingOut | null): Theme {
  const primary = branding?.primary_color || DEFAULTS.primary;
  const secondary = branding?.secondary_color || DEFAULTS.secondary;
  const background = branding?.background_color || DEFAULTS.background;
  const fontFamily = isSafeFontFamily(branding?.font_family)
    ? `'${branding!.font_family}', -apple-system, BlinkMacSystemFont, sans-serif`
    : "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif";
  const borderRadius = branding?.border_radius ?? 3;

  const baseTheme = createTheme();
  let augmentedPrimary;
  try {
    augmentedPrimary = baseTheme.palette.augmentColor({ color: { main: primary }, name: 'primary' });
  } catch {
    augmentedPrimary = baseTheme.palette.augmentColor({ color: { main: DEFAULTS.primary }, name: 'primary' });
  }
  let augmentedSecondary;
  try {
    augmentedSecondary = baseTheme.palette.augmentColor({ color: { main: secondary }, name: 'secondary' });
  } catch {
    augmentedSecondary = baseTheme.palette.augmentColor({ color: { main: DEFAULTS.secondary }, name: 'secondary' });
  }

  return createTheme({
    palette: {
      primary: augmentedPrimary,
      secondary: augmentedSecondary,
      success: {
        main: DEFAULTS.success,
        light: DEFAULTS.successLight,
      },
      error: {
        main: DEFAULTS.error,
        light: DEFAULTS.errorLight,
      },
      warning: {
        main: secondary,
        light: DEFAULTS.warningLight,
      },
      info: {
        main: DEFAULTS.info,
        light: DEFAULTS.infoLight,
      },
      background: {
        default: background,
        paper: '#ffffff',
      },
      text: {
        primary: DEFAULTS.ink,
        secondary: DEFAULTS.textSecondary,
      },
      divider: 'rgba(0,0,0,0.06)',
    },
    typography: {
      fontFamily,
      h3: {
        fontWeight: 700,
        letterSpacing: '-0.03em',
        fontSize: '2rem',
      },
      h4: {
        fontWeight: 700,
        letterSpacing: '-0.025em',
        fontSize: '1.6rem',
      },
      h5: {
        fontWeight: 600,
        letterSpacing: '-0.015em',
      },
      h6: {
        fontWeight: 600,
        letterSpacing: '-0.01em',
      },
      subtitle1: {
        color: DEFAULTS.textSecondary,
        fontSize: '1.05rem',
        lineHeight: 1.6,
      },
      body2: {
        lineHeight: 1.6,
      },
      caption: {
        letterSpacing: '0.02em',
      },
    },
    shape: {
      borderRadius,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: background,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: Math.round(borderRadius * 1.4),
            border: '1px solid rgba(0,0,0,0.07)',
            boxShadow: 'none',
            transition: 'box-shadow 0.2s ease, border-color 0.2s ease, transform 0.2s ease',
            '&:hover': {
              boxShadow: '0 8px 30px rgba(0,0,0,0.06)',
              borderColor: 'rgba(0,0,0,0.12)',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none' as const,
            fontWeight: 600,
            borderRadius: Math.round(borderRadius * 0.8),
            padding: '8px 18px',
            fontSize: '0.875rem',
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            },
          },
          outlined: {
            borderWidth: '1.5px',
            '&:hover': {
              borderWidth: '1.5px',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 500,
            borderRadius: Math.round(borderRadius * 0.6),
            fontSize: '0.75rem',
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none' as const,
            fontWeight: 600,
            fontSize: '0.9rem',
            minHeight: 48,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontWeight: 600,
            fontSize: '0.8rem',
            color: DEFAULTS.textSecondary,
            textTransform: 'uppercase' as const,
            letterSpacing: '0.04em',
            borderBottom: '2px solid rgba(0,0,0,0.06)',
          },
        },
      },
    },
  });
}
